import {
  Button,
  Checkbox,
  IconButton,
  SelectInput,
  SelectInputItem,
  TextField,
} from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@/shared/ui/atoms';

import { useEditSelectiveProcess } from '../../../hooks/useEditSelectiveProcess';
import type {
  AdmissionType,
  SelectiveProcess,
  SelectiveProcessStatus,
} from '../../../types/enrollment';

// ─── Props ───────────────────────────────────────────────────────────────────

/**
 * Props do drawer de edição do processo seletivo de uma matrícula.
 */
export interface EnrollmentSelectiveProcessDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly enrollmentId: number;
  readonly selectiveProcess: SelectiveProcess;
}

/**
 * Drawer lateral de edição do processo seletivo de uma matrícula.
 *
 * @param props.enrollmentId - ID da matrícula para a requisição PUT
 */
export function EnrollmentSelectiveProcessDrawer({
  isOpen,
  onClose,
  enrollmentId,
  selectiveProcess,
}: EnrollmentSelectiveProcessDrawerProps) {
  const { t } = useTranslation('personManagement');
  const { form, initForm, updateForm, save, isSaving } = useEditSelectiveProcess(
    enrollmentId,
    onClose,
  );

  // Re-inicializa quando o drawer abre
  useEffect(() => {
    if (isOpen) {
      initForm(selectiveProcess);
    }
  }, [isOpen, selectiveProcess, initForm]);

  // Fecha com Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Trava o scroll do body
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-none z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-none bg-base-neutral-black/medium"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Painel */}
      <div className="relative ml-auto flex h-full w-full max-w-[32rem] flex-col bg-ctx-layout-surface shadow-level3">
        {/* Header */}
        <div className="flex items-start justify-between border-b-hairline border-ctx-layout-border px-xxs py-xxxs">
          <h2 className="ts-heading-sm text-ctx-content-title">
            {t('enrollmentAcademic.selectiveProcessDrawer.title')}
          </h2>
          <IconButton
            icon={XMarkIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('enrollmentAcademic.selectiveProcessDrawer.cancelButton')}
            onClick={onClose}
          />
        </div>

        {/* Corpo scrollável */}
        <div className="flex-1 overflow-y-auto px-xxs py-xxxs">
          <div className="flex flex-col gap-xxs">
            {/* Status */}
            <SelectInput
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldStatus')}
              value={form.status}
              onValueChange={v => updateForm('status', v as SelectiveProcessStatus)}
            >
              <SelectInputItem value="pending" custom={{ root: 'text-nowrap' }}>
                {t('selectiveProcessStatus.pending')}
              </SelectInputItem>
              <SelectInputItem value="approved" custom={{ root: 'text-nowrap' }}>
                {t('selectiveProcessStatus.approved')}
              </SelectInputItem>
              <SelectInputItem value="rejected" custom={{ root: 'text-nowrap' }}>
                {t('selectiveProcessStatus.rejected')}
              </SelectInputItem>
              <SelectInputItem value="waiting_exam" custom={{ root: 'text-nowrap' }}>
                {t('selectiveProcessStatus.waiting_exam')}
              </SelectInputItem>
              <SelectInputItem value="waiting_interview" custom={{ root: 'text-nowrap' }}>
                {t('selectiveProcessStatus.waiting_interview')}
              </SelectInputItem>
            </SelectInput>

            {/* Tipo de admissão */}
            <SelectInput
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldAdmissionType')}
              value={form.admissionType}
              onValueChange={v => updateForm('admissionType', v as AdmissionType)}
            >
              <SelectInputItem value="vestibular" custom={{ root: 'text-nowrap' }}>
                {t('admissionType.vestibular')}
              </SelectInputItem>
              <SelectInputItem value="interview" custom={{ root: 'text-nowrap' }}>
                {t('admissionType.interview')}
              </SelectInputItem>
              <SelectInputItem value="transfer" custom={{ root: 'text-nowrap' }}>
                {t('admissionType.transfer')}
              </SelectInputItem>
              <SelectInputItem value="other" custom={{ root: 'text-nowrap' }}>
                {t('admissionType.other')}
              </SelectInputItem>
            </SelectInput>

            {/* Checkboxes */}
            <Checkbox
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldIsExam')}
              checked={form.isEntranceExam}
              onCheckedChange={v => {
                if (v !== 'indeterminate') updateForm('isEntranceExam', v);
              }}
            />

            {form.isEntranceExam && (
              <Checkbox
                label={t('enrollmentAcademic.selectiveProcessDrawer.fieldAttended')}
                checked={form.attendedEntranceExam}
                onCheckedChange={v => {
                  if (v !== 'indeterminate') updateForm('attendedEntranceExam', v);
                }}
              />
            )}

            <Checkbox
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldDocApproved')}
              checked={form.isDocumentationApproved}
              onCheckedChange={v => {
                if (v !== 'indeterminate') updateForm('isDocumentationApproved', v);
              }}
            />

            {/* Datas */}
            <DatePicker
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldExamDate')}
              value={form.entranceExamDate}
              onChange={v => updateForm('entranceExamDate', v)}
            />

            <DatePicker
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldInterviewDate')}
              value={form.interviewDate}
              onChange={v => updateForm('interviewDate', v)}
            />

            {/* Entrevistador */}
            <TextField
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldInterviewerName')}
              value={form.interviewerName}
              onChange={e => updateForm('interviewerName', e.target.value)}
            />

            {/* Motivo de rejeição */}
            <TextField
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldDocRejectReason')}
              value={form.documentationRejectReason}
              onChange={e => updateForm('documentationRejectReason', e.target.value)}
            />

            {/* Notas */}
            <TextField
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldExamGrade')}
              value={form.entranceExamGrade}
              onChange={e => updateForm('entranceExamGrade', e.target.value)}
              type="number"
            />

            <TextField
              label={t('enrollmentAcademic.selectiveProcessDrawer.fieldEssayGrade')}
              value={form.essayGrade}
              onChange={e => updateForm('essayGrade', e.target.value)}
              type="number"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-xxs border-t-hairline border-ctx-layout-border px-xxs py-xxxs">
          <Button hierarchy="secondary" size="sm" onClick={onClose}>
            {t('enrollmentAcademic.selectiveProcessDrawer.cancelButton')}
          </Button>
          <Button size="sm" onClick={save} isLoading={isSaving}>
            {t('enrollmentAcademic.selectiveProcessDrawer.saveButton')}
          </Button>
        </div>
      </div>
    </div>
  );
}
